import React, { useState } from "react";
import { 
  Button, 
  TextField, 
  Box, 
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
  Stack,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import ScienceIcon from "@mui/icons-material/Science";
import { acceptDemoRequest } from "../../../../Services/DemoApi";

const AcceptDemo = ({ demoId, tenantName, onSuccess }) => {
  const [startDemo, setStartDemo] = useState("");
  const [endDemo, setEndDemo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const validate = () => {
    if (!startDemo || !endDemo) {
      setError("يرجى تحديد تاريخ البداية والنهاية");
      return false;
    }
    if (new Date(endDemo) <= new Date(startDemo)) {
      setError("تاريخ النهاية يجب أن يكون بعد تاريخ البداية");
      return false;
    }
    setError("");
    return true;
  };

  const handleAccept = () => {
    if (!validate()) return;
    
    setLoading(true);
    setError("");
    acceptDemoRequest(demoId, startDemo, endDemo)
      .then(() => {
        setSuccess(true);
        setTimeout(() => {
          if (onSuccess) onSuccess();
        }, 1500);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || "حدث خطأ أثناء قبول النسخة");
      })
      .finally(() => setLoading(false));
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: '#e8f5e9',
            mb: 2,
          }}
        >
          <ScienceIcon sx={{ fontSize: 32, color: '#4caf50' }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
          قبول النسخة التجريبية
        </Typography>
        <Typography variant="body2" color="text.secondary">
          حدد فترة النسخة التجريبية للعميل
        </Typography>
      </Box>

      {/* Alert Messages */}
      {success && (
        <Alert 
          severity="success" 
          icon={<CheckCircleIcon />}
          sx={{ mb: 3, borderRadius: 2, boxShadow: 1 }}
        >
          تم قبول النسخة التجريبية بنجاح!
        </Alert>
      )}
      
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2, boxShadow: 1 }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {/* Form Card */}
      <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={3}>
            {/* Client Info */}
            {tenantName && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: '#1976d2',
                    }}
                  >
                    <PersonIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      اسم المستأجر
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {tenantName}
                    </Typography>
                  </Box>
                </Box>
                <Divider />
              </>
            )}

            {/* Date Section */}
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600,
                color: '#2e7d32',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CalendarTodayIcon fontSize="small" />
              فترة النسخة التجريبية
            </Typography>

            {/* Start Date */}
            <TextField
              fullWidth
              label="تاريخ البداية"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={startDemo}
              onChange={(e) => {
                setStartDemo(e.target.value);
                setError("");
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#4caf50',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#4caf50',
                  },
                },
              }}
            />

            {/* End Date */}
            <TextField
              fullWidth
              label="تاريخ النهاية"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={endDemo}
              onChange={(e) => {
                setEndDemo(e.target.value);
                setError("");
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#4caf50',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#4caf50',
                  },
                },
              }}
            />

            {/* Info Alert */}
            <Alert 
              severity="info" 
              sx={{ 
                borderRadius: 2,
                backgroundColor: '#e3f2fd',
              }}
            >
              <Typography variant="body2">
                سيتم تفعيل النسخة التجريبية للفترة المحددة. تأكد من صحة التواريخ قبل المتابعة.
              </Typography>
            </Alert>

            {/* Action Button */}
            <Button
              fullWidth
              variant="contained"
              color="success"
              size="large"
              onClick={handleAccept}
              disabled={loading || !startDemo || !endDemo || success}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
              sx={{
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6,
                },
              }}
            >
              {loading ? "جارٍ القبول..." : success ? "تم القبول بنجاح" : "قبول وتفعيل النسخة"}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AcceptDemo;
